import { useCallback, useState } from "react";
import { type API } from "../logic/classApi";
import { createUrl, formatApiDateString, formatApiYmdString } from "./apiCommon";
import { showErrorMessage } from "../components/appPane";
import { MESSAGES } from "../constants/const";

/** 薬剤残量情報APIのURL */
const API_CHEMICAL_VOLUME_URL = import.meta.env.VITE_API_CHEMICAL_VOLUME_URL;

/** 薬剤残量情報 */
interface VolumeData {
  /** 支所コード */
  branchCode: string;
  /** 薬剤ID */
  chemicalId: number;
  /** 実績日付 */
  volumeDate: string;
  /** 実績残量 */
  volumeData: number;
  /** 開封済み箱残量 */
  volumeOpenedBoxData: number;
  /** 空き箱残量 */
  countEmptyBox: number;
  /** 箱→貯蔵残量実績リスト */
  stockVolumeData?: number[];
  /** 実績原単位 */
  volumeIntensity: number;
  /** 作成日時 */
  createdAt?: string;
  /** 更新日時 */
  updatedAt?: string;
  /** 削除日時 */
  deletedAt?: string;
}

/**
 * 薬剤残量API
 * @param api APIモジュール
 * @param branchCode 支所コード
 * @returns
 */
export function useChemicalVolumeApi(api: API, branchCode: string) {
  const [volumes, setVolumes] = useState<VolumeData[]>();

  /**
   * 薬剤残量情報一覧取得
   * @param chemicalId 薬剤ID
   * @param startDate 開始日付
   * @param endDate 終了日付
   * @returns 薬剤残量情報データリスト
   */
  async function loadVolumes(chemicalId: number, startDate: Date, endDate: Date) {
    const params = {
      branchCode,
      chemicalId,
      startDate: formatApiDateString(startDate),
      endDate: formatApiDateString(endDate),
    };
    const reqUrl = createUrl(API_CHEMICAL_VOLUME_URL, params);
    try {
      const result = await api.getRequest<VolumeData[]>(reqUrl);
      const newVolumes = volumes?.filter(v => v.chemicalId !== chemicalId) ?? [];
      setVolumes([...newVolumes, ...result.payload]);
    } catch (e) {
      console.warn(e);
      showErrorMessage(MESSAGES.ERROR_GET);
    }
  }

  /**
   * 最新の薬剤残量情報一覧取得
   * @returns 薬剤残量情報データリスト
   */
  async function loadLastVolumes() {
    const params = { branchCode };
    const reqUrl = createUrl(API_CHEMICAL_VOLUME_URL, params);
    try {
      const result = await api.getRequest<VolumeData[]>(reqUrl);
      setVolumes(result.payload);
    } catch (e) {
      showErrorMessage(MESSAGES.ERROR_GET);
    }
  }

  /**
   * 薬剤残量情報登録・更新
   * @returns 薬剤残量情報データリスト
   */
  async function saveVolume(
    chemicalId: number,
    volumeDate: string,
    volumeData: number,
    stockVolumeData?: number[],
    volumeOpenedBoxData?: number,
    countEmptyBox?: number
  ) {
    const body = {
      branchCode,
      chemicalId,
      volumeDate,
      volumeData,
      stockVolumeData,
      volumeOpenedBoxData,
      countEmptyBox,
    };
    try {
      await api.postRequestJson(API_CHEMICAL_VOLUME_URL, body);
    } catch (e) {
      showErrorMessage(MESSAGES.ERROR_POST);
    }
    await loadLastVolumes();
  }

  const getLastVolume = useCallback(
    (chemicalId: number) =>
      volumes
        ?.filter(v => v.chemicalId === chemicalId)
        .sort((a, b) => Number(b.volumeDate) - Number(a.volumeDate))[0],
    [volumes]
  );

  const getVolume = useCallback(
    (chemicalId: number, date: Date) => {
      const ymd = formatApiYmdString(date);
      return volumes?.find(v => v.chemicalId === chemicalId && v.volumeDate === ymd);
    },
    [volumes]
  );

  return [
    { volumes, getVolume, getLastVolume },
    {
      loadVolumes,
      loadLastVolumes,
      saveVolume,
    },
  ];
}
